<template>
  <div ref="rootEl" class="dept-select">
    <!-- Selected chips or trigger -->
    <button
      class="dept-trigger"
      :class="{ open: isOpen }"
      type="button"
      @click="isOpen = !isOpen"
    >
      <div
        v-if="modelValue && modelValue.length"
        class="dept-chips"
        style="flex: 1"
      >
        <span v-for="code in modelValue" :key="code" class="dept-chip">
          {{ getLabel(code) }}
          <button
            class="dept-chip-remove"
            type="button"
            @click.stop="removeCode(code)"
          >
            ✕
          </button>
        </span>
      </div>
      <span v-else style="color: var(--ink-dim); font-size: 13px"
        >Départements</span
      >
      <span class="arrow">▾</span>
    </button>

    <!-- Dropdown -->
    <div v-if="isOpen" class="dept-dropdown">
      <input
        v-model="searchQuery"
        class="dept-search-input"
        type="text"
        placeholder="Rechercher..."
        @click.stop
      />
      <div
        v-for="dept in filteredDepts"
        :key="dept.value"
        class="dept-option"
        :class="{ selected: isSelected(dept.value) }"
        @click="toggleDept(dept.value)"
      >
        <input
          type="checkbox"
          :checked="isSelected(dept.value)"
          readonly
          tabindex="-1"
        />
        {{ dept.label }}
      </div>
      <div
        v-if="filteredDepts.length === 0"
        style="padding: 12px 10px; font-size: 12px; color: var(--ink-dim)"
      >
        Aucun résultat
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import departements from '@/datas/departements';

const props = defineProps<{
  modelValue: string[] | undefined;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string[]];
}>();

const isOpen = ref(false);
const searchQuery = ref('');
const rootEl = ref<HTMLElement>();

const MAX_SELECTIONS = 10;

onClickOutside(rootEl, () => {
  isOpen.value = false;
});

const filteredDepts = computed(() =>
  departements.filter((d) =>
    d.label.toLowerCase().includes(searchQuery.value.toLowerCase()),
  ),
);

const isSelected = (code: string) => (props.modelValue ?? []).includes(code);

const getLabel = (code: string) => {
  const d = departements.find((d) => d.value === code);
  return d ? (d.label.match(/\(([^)]+)\)/)?.[1] ?? code) : code;
};

const toggleDept = (code: string) => {
  const current = [...(props.modelValue ?? [])];
  const idx = current.indexOf(code);
  if (idx === -1) {
    if (current.length >= MAX_SELECTIONS) return;
    emit('update:modelValue', [...current, code]);
  } else {
    current.splice(idx, 1);
    emit('update:modelValue', current);
  }
};

const removeCode = (code: string) => {
  emit(
    'update:modelValue',
    (props.modelValue ?? []).filter((c) => c !== code),
  );
};
</script>
